import { DateTime } from 'luxon';
import React, { useCallback } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { RequestLog, ResponseLog } from '@/types/logs';

import InfoRow from './infoRow';
import styles from './logstyles.module.scss';

type Props = {
  request: RequestLog;
  responses: ResponseLog[];
};

const ResponseLogDetailView = ( { request, responses }: Props ) => {
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const formatTimestamp = useCallback( ( timestamp: string | undefined ) => {
    if ( !timestamp ) return 'N/A';
    const dt = DateTime.fromISO( timestamp, { zone: 'utc' } );
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();
    return zonedDt.toFormat( 'LLL d, yyyy, h:mm:ss.SSS a (ZZZZ)' );
  }, [ useLocalTimeZone ] );

  const callId = request.meta_data?.call_id;
  const requestPayload = request.meta_data?.request;

  return (
    <div>
      {/* Request Summary */}
      <div className={ styles.card }>
        <div className={ styles.header }>
          <Heading as="h2" size="md" className={ styles.title }>Request Details</Heading>
        </div>
        <InfoRow label="Call ID" value={ callId ?? 'N/A' } mono />
        <InfoRow label="Start Time" value={ formatTimestamp( request.start_time ) } />
        {request.end_time && <InfoRow label="End Time" value={ formatTimestamp( request.end_time ) } />}
        {request.correlation_id && <InfoRow label="Correlation ID" value={ request.correlation_id } mono />}
        {request.request_id && <InfoRow label="Request ID" value={ request.request_id } mono />}
        {request.client_id && <InfoRow label="Client ID" value={ request.client_id } mono />}
        {request.model_id && <InfoRow label="Model ID" value={ request.model_id } />}
      </div>

      {/* Request Payload */}
      {requestPayload !== undefined && (
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Request Payload</Heading>
          <div className={ styles.jsonContainer }>
            <pre className={ styles.jsonContent }>
              {typeof requestPayload === 'string'
                ? requestPayload
                : JSON.stringify( requestPayload, null, 2 )}
            </pre>
          </div>
        </div>
      )}

      {/* Responses */}
      <div className={ styles.card }>
        <Heading as="h2" size="md" className={ styles.sectionTitle }>
          Responses ({responses.length})
        </Heading>

        {responses.length === 0 ? (
          <p className={ styles.empty_message }>No responses found for this request.</p>
        ) : (
          responses.map( ( response, index ) => {
            const responsePayload = response.meta_data?.response;
            return (
              <div
                key={ response.id || index }
                className={ styles.response_item }
              >
                <p className={ styles.response_label }>
                  Response {index + 1}
                </p>
                {response.id && (
                  <InfoRow label="Response ID" value={ response.id } mono />
                )}
                {response.start_time && (
                  <InfoRow label="Time" value={ formatTimestamp( response.start_time ) } />
                )}
                {response.correlation_id && (
                  <InfoRow label="Correlation ID" value={ response.correlation_id } mono />
                )}
                {response.request_id && (
                  <InfoRow label="Request ID" value={ response.request_id } mono />
                )}
                {responsePayload !== undefined && (
                  <div className={ styles.payload_section }>
                    <p className={ styles.payload_label }>Payload</p>
                    <div className={ styles.jsonContainer }>
                      <pre className={ styles.jsonContent }>
                        {typeof responsePayload === 'string'
                          ? responsePayload
                          : JSON.stringify( responsePayload, null, 2 )}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          } )
        )}
      </div>
    </div>
  );
};

export default ResponseLogDetailView;
